import { useState } from "react";

import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";

import { useSessionStore } from "@/entities/session";
import { Button } from "@/shared/ui/button";
import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { Roles } from "@/entities/user";
import { numberWithSpaces } from "@/shared/lib/utils";
import { ReassignMentorshipsModal } from "./ReassignMentorshipsModal";
import { HandleSelectDPUserButton } from "@/pages/restricted/shared/main-page/ui/head/HeadDashboardProDataTable";
import { UnnasignedTgTooltip } from "@/features/UnnasignedTgTooltip";
import { SortButton } from "@/features/SortButton/SortButton";

import { type Mentor } from "@/entities/mentor/model";
import { PauseMentorProfileDropdown } from "./PauseMentorProfileDropdown";
import { useIsCurrentlyAtDashboardPro } from "@/shared/hooks";

export const MentorsColumns: ColumnDef<Mentor>[] = [
  {
    accessorKey: "login",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Логин
      </DataTableCenteredCell>
    ),
    cell: function Cell({ row }) {
      const navigate = useNavigate();
      const { user } = useSessionStore();
      const role = user?.role!;

      const isCurrentlyAtDashboardPro = useIsCurrentlyAtDashboardPro([
        Roles.Head,
      ]);

      const handleNavigate = () => {
        navigate(
          role === Roles.Head
            ? `/head/access?search=${row.original.username}`
            : `/?tab=dashboard_pro&selectedUserId=${row.original.userId._id}&selectedUserRole=mentor&selectedUserName=${row.original.username}`,
        );
      };

      return (
        <DataTableCenteredCell>
          <div className={`relative`}>
            {isCurrentlyAtDashboardPro ? (
              <HandleSelectDPUserButton
                userId={row.original.userId?._id || ""}
                userName={row.original.username}
                userRole={Roles.Mentor}
              />
            ) : (
              <Button
                onClick={handleNavigate}
                variant="link"
                className="text-blue-500 hover:underline"
              >
                {row.original.username || "N/A"}
              </Button>
            )}

            {!row.original.contacts.main && (
              <UnnasignedTgTooltip offset={-0.5} />
            )}
          </div>
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "telegram",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Telegram
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.contacts.main || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "tgReservation",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Резервный телеграм
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.contacts.reserve || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "activeStudents",
    header: () => (
      <DataTableCenteredCell>
        <SortButton title="Активных учеников" sort="activeStudentsCount" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.activeStudentsCount ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "graduatedStudent",
    header: () => (
      <DataTableCenteredCell>
        <SortButton
          title="Выпустившиxся студентов"
          sort="graduatedStudentsCount"
        />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.graduatedStudentsCount ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalIncome",
    header: () => (
      <DataTableCenteredCell>
        <SortButton title="Прибыль" sort="totalIncome" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const totalIncome = row.original.totalIncome;

      return (
        <DataTableCenteredCell>
          {totalIncome ? `$ ${numberWithSpaces(totalIncome)}` : "N/A"}
        </DataTableCenteredCell>
      );
    },
  },
  {
    id: "actions",
    header: () => (
      <DataTableCenteredCell className="text-thColor">
        Действия
      </DataTableCenteredCell>
    ),
    cell: function Cell({ row }) {
      const { userId } = row.original;
      const [open, setOpen] = useState(false);

      if (!userId) return null;

      return (
        <DataTableCenteredCell className="flex-row">
          <ReassignMentorshipsModal mentorId={row.original.userId._id} />
          <PauseMentorProfileDropdown
            mentorUserId={userId._id}
            open={open}
            setOpen={setOpen}
          />
        </DataTableCenteredCell>
      );
    },
  },
];
